import PropTypes from 'prop-types';
import { Button } from 'anf-core-react';

/**
 * @name textProp
 * @public
 * @description PropType for text objects that contain a key and value pair.
 * Used for internationalization and localization of text content.
 * @type {PropTypes.Validator<object>}
 */
export const textProp = PropTypes.shape({
  key: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
});

/**
 * @name labelValuePairProp
 * @public
 * @description PropType for objects containing a label (as textProp) and an optional value.
 * Used for form fields, display items, or configuration objects.
 * @type {PropTypes.Validator<object>}
 */
export const labelValuePairProp = PropTypes.shape({
  label: textProp.isRequired,
  value: PropTypes.string,
});

/**
 * @name optionProp
 * @public
 * @description PropType for option items used in select menus or choice components.
 * Contains the option value, display text, and optional description.
 * @type {PropTypes.Validator<object>}
 */
export const optionProp = PropTypes.shape({
  optionValue: PropTypes.string.isRequired,
  text: textProp.isRequired,
  description: textProp,
});

/**
 * @name linkProp
 * @public
 * @description PropType for link objects containing display text, URL, and optional description.
 * Used for navigation links, buttons, and anchors.
 * @type {PropTypes.Validator<object>}
 */
export const linkProp = PropTypes.shape({
  text: textProp,
  url: PropTypes.string,
  description: textProp,
});

/**
 * @name fieldListProp
 * @public
 * @description PropType for lists of form fields.
 * @type {PropTypes.Validator<object>}
 */
export const fieldListProp = PropTypes.arrayOf(PropTypes.shape({
  label: textProp,
  description: textProp,
  optionList: PropTypes.arrayOf(optionProp),
}));

/**
 * @name buttonStateProp
 * @public
 * @description PropType for defining different visual states of a button component.
 * Requires nodes for initial, processing, error, and success states.
 * @type {PropTypes.Validator<object>}
 */
export const buttonStateProp = PropTypes.shape({
  initial: PropTypes.node.isRequired,
  processing: PropTypes.node.isRequired,
  error: PropTypes.node.isRequired,
  success: PropTypes.node.isRequired,
});

/**
 * @name multiSelectProp
 * @public
 * @description PropType for multi-select form field components.
 * Includes name, label, and a list of options, with optional description and error message.
 * @type {PropTypes.Validator<object>}
 */
export const multiSelectProp = PropTypes.shape({
  name: PropTypes.string.isRequired,
  label: textProp.isRequired,
  description: textProp,
  error: textProp,
  optionList: PropTypes.arrayOf(optionProp),
});

/**
 * @name inputProp
 * @public
 * @description PropType for standard input form field components.
 * Includes name, label, and optional description and error message.
 * @type {PropTypes.Validator<object>}
 */
export const inputProp = PropTypes.shape({
  name: PropTypes.string.isRequired,
  label: textProp.isRequired,
  description: textProp,
  error: textProp,
});

/**
 * @name formGroupProp
 * @public
 * @description PropType for grouping related form fields together.
 * Contains a label, list of fields (multi-select or input), and optional error message.
 * @type {PropTypes.Validator<object>}
 */
export const formGroupProp = PropTypes.shape({
  label: textProp.isRequired,
  fieldList: PropTypes.arrayOf(PropTypes.oneOfType([multiSelectProp, inputProp])).isRequired,
  error: textProp,
});

/**
 * @name refProp
 * @public
 * @description PropType for React refs, supporting both callback and object ref patterns.
 * Includes SSR compatibility workaround for Element type checking.
 * @type {PropTypes.Validator<Function|Object>}
 */
export const refProp = PropTypes.oneOfType([
  PropTypes.func,
  PropTypes.shape({
    current: PropTypes.instanceOf(
      /*
        Note: SSR workaround https://stackoverflow.com/questions/48007326/what-is-the-correct-proptype-for-a-ref-in-react
      */
      typeof Element === 'undefined' ? {} : Element,
    ),
  }),
]);

/**
 * @name dsButtonProp
 * @public
 * @description Custom prop validator that checks if prop is a Button component from anf-core-react.
 * @param {Object} props - Component props object
 * @param {string} propName - Name of the prop to validate
 * @returns {Error|undefined} Returns an Error if validation fails, undefined otherwise
 */
export function dsButtonProp(props, propName) {
  const prop = props[propName];
  if (!prop) return;
  if (prop?.type !== Button) {
    throw new Error(`ButtonState prop ${propName} is not of type "Button"`);
  }
}

/**
 * @name requiredDSButtonProp
 * @public
 * @description Custom prop validator that ensures a required Button component is provided.
 * Extends dsButtonProp by also checking if the prop exists at all.
 * @param {Object} props - Component props object
 * @param {string} propName - Name of the prop to validate
 * @returns {Error|undefined} Returns an Error if validation fails, undefined otherwise
 */
export function requiredDSButtonProp(props, propName) {
  const prop = props[propName];
  if (!prop) { throw new Error(`ButtonState prop ${propName} is required`); }
  dsButtonProp(props, propName);
}

/**
 * @name ofTypes
 * @public
 * @description Helper function to validate if a prop matches one of the provided types.
 * @param {Array<*>} types - Array of valid types to check against
 * @param {*} prop - The prop to validate
 * @throws {Error} Throws an error if the prop type is not included in the types array
 * @returns {void}
 */
export function ofTypes(types, prop) {
  const findType = (type) => {
    if (prop.type) return type === prop.type;
    return type === prop;
  };

  if (!types?.find(findType)) {
    throw Error(
      `${prop?.name} is not the proper types. Type of ${prop?.type} was passed`,
    );
  }
}
